import {
  AnimatedElement,
  Box,
  MediaCard,
  ScrollArea,
  Text,
} from "@prismicio/editor-ui"
import {
  type KeyboardEvent,
  type MouseEvent,
  type ReactNode,
  type RefObject,
  useRef,
} from "react"

import { useSelectionManager } from "../../SelectionManager"
import { useMediaLibrary } from "./MediaLibraryContext"

export function MediaList() {
  const {
    onScrollToBottom,
    media,
    isUploading,
    uploadingFiles,
    isSearchingMedia,
    isMediaRevalidationInProgress,
    setOpenMediaSection,
    isFetchingMedia,
  } = useMediaLibrary()
  const selectionManager = useSelectionManager()

  const scrollAreaRef = useRef<HTMLDivElement>(null)

  function onAddTagClick(mediaId: string) {
    selectionManager.toggleSelection({
      id: mediaId,
      isSingleSelection: true,
    })

    setOpenMediaSection("tags")
  }

  if (isUploading || isSearchingMedia || isMediaRevalidationInProgress) {
    scrollAreaRef.current?.scrollTo({
      top: 0,
      behavior: "instant",
    })
  }

  function onSelectItem(
    id: string,
    event: MouseEvent | KeyboardEvent,
    type: "single" | "multi",
  ) {
    const ids = event.shiftKey ? media.map(({ id }) => id) : undefined

    selectionManager.toggleSelection({
      id,
      isSingleSelection: type === "single",
      metaKey: event.metaKey,
      shiftKey: event.shiftKey,
      ids,
    })
  }

  return (
    <MediaListScrollArea
      onScrollToBottom={onScrollToBottom}
      scrollAreaRef={scrollAreaRef}
    >
      {media.map((media) => (
        <MediaCard
          key={media.id}
          aria-label="media item"
          {...media}
          filenameHighlight={media.search_highlight?.filename}
          loading={isSearchingMedia}
          status={uploadingFiles.find((file) => file.id === media.id)?.status}
          selected={selectionManager.isSelected(media.id)}
          onCardClick={(event) => onSelectItem(media.id, event, "single")}
          onCheckedChange={(_, event) => onSelectItem(media.id, event, "multi")}
          onAddTagClick={() => onAddTagClick(media.id)}
        />
      ))}

      <Box gridColumn="1 / -1" justifyContent="center">
        <AnimatedElement animation={{ name: "fade" }}>
          {isFetchingMedia && (
            <Text align="center" color="grey11" sx={{ marginBlock: 8 }}>
              Loading more items ...
            </Text>
          )}
        </AnimatedElement>
      </Box>
    </MediaListScrollArea>
  )
}

interface MediaListScrollAreaProps {
  children: ReactNode
  scrollAreaRef?: RefObject<HTMLDivElement>
  onScrollToBottom?: () => void
}

export function MediaListScrollArea(props: MediaListScrollAreaProps) {
  const { children, scrollAreaRef, onScrollToBottom } = props

  const gap = 16
  const maxColumns = 4
  const mediaCardMinWidth = "250px"
  // Scale max width based on the container size so that we never have more than 4 columns
  const mediaCardMaxWidth = `calc((100% - ${
    gap * (maxColumns - 1)
  }px) / ${maxColumns})`

  return (
    <ScrollArea
      aria-label="Media list"
      ref={scrollAreaRef}
      style={{
        display: "grid",
        gridTemplateColumns: `repeat(auto-fill, minmax(max(${mediaCardMinWidth}, ${mediaCardMaxWidth}), 1fr))`,
        gridTemplateRows: "1fr 1fr 1fr ",
        gap,
        padding: 2,
        paddingBottom: 16,
        position: "relative",
      }}
      onScrollToBottom={onScrollToBottom}
      threshold={500}
    >
      {children}
    </ScrollArea>
  )
}
